import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { AxiosError } from 'axios'

import { ConfirmBeneficiaryModalProps, ConfirmBeneficiaryStatus } from './ConfirmBeneficiaryModal.types'
import {
  ActionModal,
  Modal,
  Text,
  Spacer,
  AcknowledgeModal,
  FlagAvatar,
  Flex,
  CardContent,
  Loader
} from '@percent/lemonade'
import { useMutation, useQuery } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import styles from './ConfirmBeneficiaryModal.module.scss'

export function ConfirmBeneficiaryModal({ isOpen, onClose, disbursement, refreshData }: ConfirmBeneficiaryModalProps) {
  const { disbursementsService } = useServices()
  const { t } = useTranslation()

  const [status, setStatus] = useState<ConfirmBeneficiaryStatus | null>(null)

  const {
    data: beneficiaryDetails,
    isLoading: isLoadingDetails,
    errorMessage: fetchErrorMessage
  } = useQuery(['beneficiary-details', disbursement.id], disbursementsService.getBeneficiaryDetails, disbursement.id, {
    enabled: isOpen
  })

  const [{ isPending, error: confirmError, errorMessage: confirmErrorMessage }, { apiFunc: confirmBeneficiary }] =
    useMutation(
      disbursementsService.confirmBeneficiary,
      () => {
        setStatus(ConfirmBeneficiaryStatus.SUCCESS)
        refreshData?.()
      },
      () => {
        setStatus(ConfirmBeneficiaryStatus.ERROR)
      }
    )

  // Show the API's own reason for client (4xx) errors; use the generic message for server (5xx) errors.
  const confirmErrorResponse = (confirmError as AxiosError<{ error?: { message?: string; reasons?: string[] } }> | null)
    ?.response
  const confirmErrorDescription =
    confirmErrorResponse?.status && confirmErrorResponse.status < 500
      ? confirmErrorResponse.data?.error?.reasons?.[0] ??
        confirmErrorResponse.data?.error?.message ??
        confirmErrorMessage
      : confirmErrorMessage

  const successModal = status === ConfirmBeneficiaryStatus.SUCCESS && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.confirmBeneficiary.success.title')}
      buttonText={t('button.close')}
      handleClose={onClose}
      viewTestId="confirm-beneficiary-success-modal"
      buttonTestId="close-confirm-beneficiary-success-modal"
    />
  )

  const errorModal = (status === ConfirmBeneficiaryStatus.ERROR || fetchErrorMessage) && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.confirmBeneficiary.error.title')}
      description={status === ConfirmBeneficiaryStatus.ERROR ? confirmErrorDescription : fetchErrorMessage}
      buttonText={t('button.close')}
      handleClose={onClose}
      viewTestId="confirm-beneficiary-error-modal"
      buttonTestId="close-confirm-beneficiary-error-modal"
    />
  )

  return (
    <Modal open={isOpen} onClose={onClose}>
      {successModal || errorModal || (
        <ActionModal
          title={t('dialog.confirmBeneficiary.header')}
          primaryButtonText={t('button.confirm')}
          loadingText={t('button.confirming')}
          secondaryButtonText={t('button.cancel')}
          handleClose={onClose}
          handleSubmit={() => confirmBeneficiary(disbursement.id)}
          primaryBtnTestId="confirm-beneficiary-submit-button"
          secondaryBtnTestId="confirm-beneficiary-cancel-button"
          disabled={isLoadingDetails || isPending || !beneficiaryDetails}
          loading={isPending}
        >
          <div>
            {isLoadingDetails ? (
              <Loader />
            ) : (
              <>
                <Text size="small">{t('dialog.confirmBeneficiary.description')}</Text>
                <Spacer axis="vertical" size={6} />
                <Flex direction="column" gap={4}>
                  <CardContent
                    className={styles.cardContent}
                    title={t('dialog.confirmBeneficiary.fields.organization')}
                  >
                    {disbursement.organisationName}
                  </CardContent>
                  <CardContent className={styles.cardContent} title={t('dialog.confirmBeneficiary.fields.country')}>
                    <Flex gap={8}>
                      <FlagAvatar code3={disbursement.organisationCountryCode} />
                      {beneficiaryDetails?.organisationCountry}
                    </Flex>
                  </CardContent>
                  <CardContent
                    className={styles.cardContent}
                    title={t('dialog.confirmBeneficiary.fields.accountNumber')}
                  >
                    {beneficiaryDetails?.wireTransferDetails?.accountNumber}
                  </CardContent>
                  {beneficiaryDetails?.wireTransferDetails?.bankCode && (
                    <CardContent className={styles.cardContent} title={t('dialog.confirmBeneficiary.fields.sortCode')}>
                      {beneficiaryDetails?.wireTransferDetails.bankCode}
                    </CardContent>
                  )}
                </Flex>
                <Spacer axis="vertical" size={6} />
                <Text size="small">{t('dialog.confirmBeneficiary.confirmation')}</Text>
              </>
            )}
          </div>
        </ActionModal>
      )}
    </Modal>
  )
}
